import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GlassCard, CardHeader, Button, Icon, KpiCard, BarList, DataTable, DonutChart, LineAreaChart, StatusPill, Tabs, chart } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { useApiData } from '../data/useApiData';
import { useRegistryStatus } from '../data/useVendorState';
import { surepassUsageService, selfRegularizationService } from '../../../../services/apiService';
import { useAuth } from '../../../../contexts/AuthContext';
import { formatInr, RUN_STATUS_PILL, checkByKey, toneFor } from '../data/selfRegularization';
import { SOURCE_COLOR, callCost, groupBars, gstCaption, inferredNote, outcomeLabel, sourceSlices } from '../data/walletSpend';
import { formatShortDate, formatDateTime } from '../data/format';
import { RegistryNotice, CheckChip } from '../components/SelfRegularizationBits';
import { CreditFormModal, CreditHistory, CreditsCard, RemoveCreditModal } from '../components/WalletCredits';

/**
 * Wallet — what is left of the Surepass credits, and every rupee the provider APIs have cost.
 *
 * At the top, the credits an admin recorded off the Surepass console (GET /surepass-usage/credits):
 * nothing can read that wallet, so the balance here is what was recorded less the calls this app has
 * made since — each at the price in effect when it was made, plus the GST Surepass deducts with it.
 *
 * Below it, the money itself (GET /surepass-usage/spend): every Surepass and Google Vision call made
 * by the registration form, verification runs, document reads and the check scripts. A call is
 * charged when the provider answered it; a refused or failed call is free, and a cache hit never
 * issues a request so it is not there at all. Rupee figures include GST, with the rate-card amount
 * and the tax named beside them.
 *
 * Last, "Verification runs" (GET /self-regularization/spend) breaks down the part of that money the
 * runs spent, at the rate card. It is a subset of the total above and is never added to it.
 */

const OUTCOME_TONE = {
  success: 'text-cos-teal-deep',
  not_found: 'text-cos-status-amber',
  forbidden: 'text-cos-status-amber',
  auth: 'text-cos-status-coral',
  upstream: 'text-cos-status-coral',
  timeout: 'text-cos-status-coral',
  network: 'text-cos-status-coral',
  bad_response: 'text-cos-status-coral',
};

const COST_TONE = {
  cost: 'mono font-semibold text-cos-ink',
  free: 'text-cos-ink-muted',
  unpriced: 'font-semibold text-cos-status-amber',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (key) => {
  const [y, m] = String(key).split('-').map(Number);
  return m ? `${MONTHS[m - 1]} ${String(y).slice(2)}` : key;
};

const num = (n) => Number(n || 0).toLocaleString('en-IN');

const RANGES = [
  { key: 'days', label: 'Last 30 days' },
  { key: 'months', label: 'By month' },
];

/**
 * Run spend per check, ranked the way BarList ranks — but in rupees, with each check in its own hue
 * (the hue is how every regularization screen names a check).
 */
function CheckSpendList({ items }) {
  const max = Math.max(...items.map((c) => c.cost_inr), 0) || 1;
  return (
    <div className="space-y-3">
      {items.map((c) => {
        const tone = toneFor(c.key);
        const def = checkByKey(c.key);
        return (
          <div key={c.key}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-[13px]">
              <span className="flex min-w-0 items-center gap-1.5 font-medium text-cos-ink">
                <Icon name={def?.icon || 'fact_check'} className={`text-[15px] ${tone.text}`} />
                <span className="truncate">{c.label}</span>
              </span>
              <span className="flex shrink-0 items-baseline gap-2">
                <span className="text-2xs text-cos-ink-muted">
                  {num(c.vendors)} vendor{c.vendors === 1 ? '' : 's'} · {num(c.calls)} call{c.calls === 1 ? '' : 's'}
                </span>
                <span className="mono font-semibold text-cos-ink">{formatInr(c.cost_inr)}</span>
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[rgba(70,80,72,0.07)]">
              <div className={`h-full rounded-full transition-[width] duration-700 ${tone.bar}`} style={{ width: `${(c.cost_inr / max) * 100}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SectionHeading({ title, text }) {
  return (
    <div className="mt-2 px-1">
      <h2 className="text-[17px] font-bold tracking-tight text-cos-ink">{title}</h2>
      <p className="mt-0.5 max-w-[720px] text-[12.5px] leading-[18px] text-cos-ink-secondary">{text}</p>
    </div>
  );
}

function ErrorCard({ message, onRetry }) {
  return (
    <GlassCard>
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <Icon name="cloud_off" className="text-[32px] text-cos-status-coral" />
        <p className="text-sm text-cos-status-coral">{message}</p>
        <Button variant="secondary" icon="refresh" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </GlassCard>
  );
}

export function WalletPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const usage = useApiData(() => surepassUsageService.spend(), [], { cacheKey: 'surepass:spend' });
  const credits = useApiData(() => surepassUsageService.credits(), [], { cacheKey: 'surepass:credits' });
  const spend = useApiData(() => selfRegularizationService.spend(), [], { cacheKey: 'selfreg:spend' });
  const registry = useRegistryStatus();
  const [range, setRange] = useState('days');
  // Which credit is being recorded ('opening_balance' | 'top_up'), and which one is being removed.
  const [recording, setRecording] = useState(null);
  const [removing, setRemoving] = useState(null);
  const u = usage.data?.data;
  const s = spend.data?.data;
  const c = credits.data?.data;

  const refreshMoney = () => {
    usage.refetch();
    credits.refetch();
  };

  usePageChrome(
    () => ({
      title: 'Wallet',
      subtitle: 'What is left of the Surepass credits, and every rupee the registry and document-reading APIs have cost',
      actions: (
        <Button
          variant="secondary"
          icon="refresh"
          onClick={() => {
            usage.refetch();
            credits.refetch();
            spend.refetch();
          }}
        >
          Refresh
        </Button>
      ),
    }),
    [usage.refetch, credits.refetch, spend.refetch]
  );

  const daily = useMemo(() => (u?.by_day ?? []).map((x) => ({ name: formatShortDate(x.date), Spend: x.cost_inr, Calls: x.calls })), [u]);
  const monthly = useMemo(() => (u?.by_month ?? []).map((x) => ({ name: monthLabel(x.month), Spend: x.cost_inr, Calls: x.calls })), [u]);
  const spendSpark = useMemo(() => (u?.by_day ?? []).map((x) => x.cost_inr), [u]);
  const billableSpark = useMemo(() => (u?.by_day ?? []).map((x) => x.billable), [u]);
  const apiBars = useMemo(() => groupBars(u?.by_group), [u]);
  const sources = useMemo(() => sourceSlices(u?.by_source), [u]);
  const colorForSource = useMemo(() => {
    const byName = new Map(sources.data.map((d) => [d.name, SOURCE_COLOR[d.source]]));
    return (name) => byName.get(name);
  }, [sources]);

  const attributed = inferredNote(u?.by_source);
  const hasCalls = (u?.calls ?? 0) > 0;
  // Real money exists once any call reached a provider, or the registry answering now is real.
  const live = hasCalls || registry.provider === 'surepass';

  const callColumns = [
    { key: 'when', header: 'When', render: (r) => <span className="whitespace-nowrap text-cos-ink-secondary">{formatDateTime(r.called_at)}</span> },
    {
      key: 'source',
      header: 'Source',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: SOURCE_COLOR[r.source] || chart.axis }} />
          <span className="font-medium text-cos-ink">{r.source_label}</span>
          {r.inferred_source && (
            <span className="text-2xs text-cos-ink-muted" title="Recorded before the source was kept — attributed by endpoint and vendor">
              inferred
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'api',
      header: 'API',
      render: (r) => (
        <div className="min-w-[170px]">
          <div className="font-semibold text-cos-ink">{r.group_label}</div>
          <div className="mono text-2xs text-cos-ink-muted">{r.endpoint}</div>
        </div>
      ),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (r) => (
        <span className="whitespace-nowrap">
          <span className={`font-semibold ${OUTCOME_TONE[r.outcome] || 'text-cos-ink-secondary'}`}>{outcomeLabel(r.outcome)}</span>
          <span className="mono ml-1.5 text-2xs text-cos-ink-muted">{r.http_status ?? '—'}</span>
        </span>
      ),
    },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      render: (r) => {
        const cost = callCost(r);
        return (
          <span className={`whitespace-nowrap ${COST_TONE[cost.tone]}`} title={cost.title ?? undefined}>
            {cost.label}
          </span>
        );
      },
    },
  ];

  const runs = s?.runs ?? { total: 0, running: 0, completed: 0, approved: 0 };
  const noRuns = !!s && runs.total === 0 && (s.mock_runs ?? 0) === 0;
  const runsCaption = [
    `${num(runs.completed)} completed`,
    runs.running > 0 ? `${num(runs.running)} verifying` : null,
    s?.mock_runs > 0 ? `${num(s.mock_runs)} mock, free` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const runColumns = [
    {
      key: 'name',
      header: 'Run',
      render: (r) => (
        <div className="min-w-[180px]">
          <Link
            to={`/campaigning-tool/regularization/self/runs/${r.id}`}
            onClick={(e) => e.stopPropagation()}
            className="line-clamp-2 font-semibold text-cos-ink hover:text-cos-teal-deep hover:underline"
          >
            {r.name}
          </Link>
          <div className="mono text-2xs text-cos-ink-muted">#{r.id}</div>
        </div>
      ),
    },
    { key: 'when', header: 'Started', render: (r) => <span className="whitespace-nowrap text-cos-ink-secondary">{formatDateTime(r.created_at)}</span> },
    {
      key: 'checks',
      header: 'Checks',
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.checks || []).map((k) => (
            <CheckChip key={k} checkKey={k} size="sm" />
          ))}
        </div>
      ),
    },
    { key: 'vendors', header: 'Vendors', align: 'right', render: (r) => <span className="mono text-cos-ink">{num(r.vendors)}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const p = RUN_STATUS_PILL[r.status] || { pill: 'draft', label: r.status };
        return <StatusPill status={p.pill} label={p.label} />;
      },
    },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      render: (r) =>
        r.provider === 'mock' ? (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="text-cos-ink-muted">Free</span>
            <span className="rounded-full bg-cos-beige-light px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-cos-beige-ink" title="This run used the mock registry — nothing was billed">
              mock
            </span>
          </span>
        ) : (
          <span className="mono font-semibold text-cos-ink">{formatInr(r.cost_inr)}</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* Only a deployment with no real money anywhere gets the note; it names the mock registry, nothing else. */}
      {u && !live && <RegistryNotice status={registry} />}

      {/* ---- What is left ------------------------------------------------------------------------- */}

      <CreditsCard
        view={c}
        loading={credits.loading}
        error={credits.error}
        isAdmin={isAdmin}
        onRecord={(kind) => setRecording(kind)}
        onRetry={credits.refetch}
      />

      <CreditHistory view={c} isAdmin={isAdmin} onRemove={(row) => setRemoving(row)} />

      {/* ---- Every rupee, every source ------------------------------------------------------------ */}

      {usage.loading && !u ? (
        <GlassCard>
          <p className="py-10 text-center text-sm text-cos-ink-secondary">Adding up every call the providers were paid for…</p>
        </GlassCard>
      ) : usage.error && !u ? (
        <ErrorCard message={usage.error} onRetry={usage.refetch} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              icon="payments"
              label="Spent"
              value={formatInr(u?.total_inr ?? 0)}
              caption={gstCaption(u?.before_gst?.total_inr, u?.gst?.total_inr) ?? 'Every answered call, from every screen'}
              tone="teal"
              sparkline={spendSpark}
            />
            <KpiCard
              icon="calendar_month"
              label="This month"
              value={formatInr(u?.this_month_inr ?? 0)}
              caption={gstCaption(u?.before_gst?.this_month_inr, u?.gst?.this_month_inr) ?? 'Calls since the 1st'}
              tone="teal"
            />
            <KpiCard
              icon="today"
              label="Today"
              value={formatInr(u?.today_inr ?? 0)}
              caption={gstCaption(u?.before_gst?.today_inr, u?.gst?.today_inr) ?? 'Calls made today'}
              tone="teal"
            />
            <KpiCard
              icon="receipt_long"
              label="Billable calls"
              value={num(u?.billable_calls ?? 0)}
              caption={`${num(u?.free_calls ?? 0)} refused or failed, free`}
              tone="neutral"
              sparkline={billableSpark}
            />
          </div>

          {!hasCalls ? (
            <GlassCard>
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <Icon name="receipt_long" className="text-[34px] text-cos-ink-secondary/50" />
                <p className="max-w-[460px] text-sm text-cos-ink-secondary">
                  No provider call recorded yet. The registration form, verification runs, document reads and the check scripts all show here from their
                  first call.
                </p>
              </div>
            </GlassCard>
          ) : (
            <>
              <GlassCard>
                <CardHeader
                  title="Spend over time"
                  subtitle={range === 'days' ? 'Rupees a day, GST included — each day on its own' : 'Rupees a month since the first call, GST included'}
                  icon={<Icon name="monitoring" className="text-[20px] text-cos-teal-deep" />}
                  action={<Tabs items={RANGES} value={range} onChange={setRange} size="sm" />}
                />
                {/* Bars, never a curve: one day's calls say nothing about the day beside it. */}
                <LineAreaChart
                  data={range === 'days' ? daily : monthly}
                  xKey="name"
                  height={240}
                  yWidth={64}
                  yFormatter={(v) => formatInr(v)}
                  series={[{ key: 'Spend', name: 'Spend', color: chart.teal, bar: true }]}
                />
              </GlassCard>

              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                <GlassCard>
                  <CardHeader
                    title="Spend by API"
                    subtitle="Each API at its price plus GST — refused and failed calls are free"
                    icon={<Icon name="api" className="text-[20px] text-cos-teal-deep" />}
                  />
                  <BarList items={apiBars} formatValue={(v) => formatInr(v)} />
                </GlassCard>

                <GlassCard>
                  <CardHeader
                    title="Spend by source"
                    subtitle={sources.unit === 'cost' ? 'Which screen the money went through' : 'Calls per screen — nothing is priced yet'}
                    icon={<Icon name="donut_small" className="text-[20px] text-cos-teal-deep" />}
                  />
                  <DonutChart
                    data={sources.data}
                    height={170}
                    colorFor={colorForSource}
                    centerValue={sources.unit === 'cost' ? formatInr(u.total_inr) : num(u.calls)}
                    centerLabel={sources.unit === 'cost' ? 'spent' : 'calls'}
                    formatValue={sources.unit === 'cost' ? (v) => formatInr(v) : undefined}
                  />
                  {attributed && <p className="mt-4 text-[12px] leading-[18px] text-cos-ink-secondary">{attributed}</p>}
                </GlassCard>
              </div>

              <GlassCard padded={false}>
                <div className="px-5 pt-5">
                  <CardHeader
                    title="Recent calls"
                    subtitle="The last 50 provider calls — which screen, which API, what came back and what it took from the wallet"
                    icon={<Icon name="history" className="text-[20px] text-cos-teal-deep" />}
                  />
                </div>
                <DataTable columns={callColumns} rows={u?.recent ?? []} rowKey={(r) => r.id} emptyLabel="No calls recorded yet." />
              </GlassCard>
            </>
          )}
        </>
      )}

      <p className="px-1 text-[12px] leading-[18px] text-cos-ink-secondary">
        A call is charged when the provider answered it — HTTP 200/202, or a 400/422 &ldquo;not found&rdquo; answer. A refused call (401/403/404, or an API
        not enabled), a timeout, a network error or a provider error (5xx) is free. Each call is priced at the rate card in effect when it was made;
        calls recorded before prices were kept use the current rate card. Surepass deducts {u?.gst?.percent ?? 18}% GST on top of its rate card, so the
        rupee figures here include it — Google Vision is billed by Google and carries none, and never comes out of the Surepass credits. Results served
        from cache never reach a provider and are not counted. Days follow the server clock (UTC).
      </p>

      {/* ---- Verification runs: a part of the money above ----------------------------------------- */}

      <SectionHeading
        title="Verification runs"
        text="The part of the spend above that verification runs made, by run and by check, at the rate card before GST. It is already in the total above — never added to it."
      />

      {spend.error && !s ? (
        <ErrorCard message={spend.error} onRetry={spend.refetch} />
      ) : spend.loading && !s ? (
        <GlassCard>
          <p className="py-8 text-center text-sm text-cos-ink-secondary">Loading verification runs…</p>
        </GlassCard>
      ) : noRuns ? (
        <GlassCard>
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Icon name="fact_check" className="text-[30px] text-cos-ink-secondary/50" />
            <p className="max-w-[440px] text-sm text-cos-ink-secondary">No verification run yet. A run&apos;s registry calls show here and in the totals above from its first call.</p>
            <Button variant="secondary" icon="arrow_forward" onClick={() => navigate('/campaigning-tool/regularization/self')}>
              Vendor Regularization
            </Button>
          </div>
        </GlassCard>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              icon="payments"
              label="Spent by runs"
              value={formatInr(s?.total_inr ?? 0)}
              caption={
                registry.pricesConfigured
                  ? 'Before GST — included in the total above'
                  : 'At placeholder prices — the rate card is not set, so the total above leaves these calls unpriced'
              }
              tone="neutral"
            />
            <KpiCard icon="play_circle" label="Runs" value={num(runs.total)} caption={runsCaption || 'None yet'} tone="neutral" />
            <KpiCard icon="verified_user" label="Vendors verified" value={num(s?.vendors_verified ?? 0)} caption={`${num(s?.calls ?? 0)} registry calls made by runs`} tone="neutral" />
            <KpiCard
              icon="person_search"
              label="Avg per vendor"
              value={formatInr(s?.avg_cost_per_vendor_inr ?? 0)}
              caption="Run spend ÷ vendors a registry answered for"
              tone="neutral"
            />
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
            <GlassCard>
              <CardHeader
                title="Run spend by check"
                subtitle="GST, PAN, bank, Udyam — at the rate card each run was created with"
                icon={<Icon name="donut_small" className="text-[20px] text-cos-teal-deep" />}
              />
              {s?.by_check?.length ? <CheckSpendList items={s.by_check} /> : <p className="py-10 text-center text-sm text-cos-ink-secondary">Nothing spent yet.</p>}
            </GlassCard>

            <GlassCard padded={false}>
              <div className="px-5 pt-5">
                <CardHeader
                  title="Recent runs"
                  subtitle="The last ten verification runs and what each one cost"
                  icon={<Icon name="history" className="text-[20px] text-cos-teal-deep" />}
                  action={
                    <Button variant="secondary" size="sm" icon="arrow_forward" onClick={() => navigate('/campaigning-tool/regularization/self')}>
                      All runs
                    </Button>
                  }
                />
              </div>
              <DataTable
                columns={runColumns}
                rows={s?.recent_runs ?? []}
                rowKey={(r) => r.id}
                onRowClick={(r) => navigate(`/campaigning-tool/regularization/self/runs/${r.id}`)}
                emptyLabel="No runs yet."
              />
            </GlassCard>
          </div>
        </>
      )}

      <CreditFormModal open={Boolean(recording)} kind={recording} onClose={() => setRecording(null)} onSaved={refreshMoney} />
      <RemoveCreditModal entry={removing} onClose={() => setRemoving(null)} onRemoved={refreshMoney} />
    </div>
  );
}

export default WalletPage;
